import { React, useEffect, useState } from 'react';
import { selectFerpaState } from '../../../../../../features/authentication/authSlice';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { faChartArea, faMessagesQuestion } from '@fortawesome/pro-solid-svg-icons';
import { useSelector } from 'react-redux';
import ReportOptions from '../../components/reportOptions';
import TableSkeleton from '../../../../../../components/SkeletonLoader/TableSkeleton';
import { ResourceUtils } from '../../../../../assignments/components/questionDetailComponents/ResourceUtils';
import { DateFormatter } from '../../../../../../utils/DateFormatter';
import { colorCode } from '../../../../utils/constants';
import { useGetSingleStudentAssignmentsScoreMutation } from '../../../../../../features/authentication/reportApiSlice';
import CustomToast from '../../../../../../components/Toast';
import {ScoreCalculator} from '../../../../utils/ScoreCalculator';
import { useReportContext } from '../../../../components/reportContext';
import ReportHeader from '../../components/reportHeader';
import FERPA from '../../../../../../components/FERPA/FerpaComponent';
import { convertToCSV, downloadCSV, reorderObjectProperties } from '../../../../../assignments/utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  useGetTeacherReportOptionsMutation
} from '../../../../../../features/authentication/persistApiSlice';
import ProgressButton from '../../../../../../components/ProgressButton';
import usePrintPdfGenerator from '../../../../../../utils/PrintPdfGenerator';
import PageSize from '../../../../../../enums/pageSize';
import ActivityTypes from '../../../../../../enums/activityTypes';

const SingleStudentScoreInfo = () => {
  const navigate = useNavigate();
  const ferpaEnabled = useSelector(selectFerpaState);
  const { selectedStudentId, selectedCourseId, startDate, endDate, courseName, studentName, isAssessment } = useReportContext();

  const [reportDate, setReportDate] = useState('');
  const [expand, setExpand] = useState(false);
  const [isLoadingReportData, setIsLoadingReportData] = useState(true);
  const [sortId, setSortId] = useState('description');
  const [isAscending, setIsAscending] = useState(true);
  const [assignmentDetails, setAssignmentDetails] = useState([]);
  const [unansweredAsIncorrect, setUnansweredAsIncorrect] = useState(null);
  const [retryScore, setRetryScore] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [displayId, setDisplayId] = useState(null);
  const [isCsvGenerating, setIsCsvGenerating] = useState(false);
  const [studentScoreDetails, setStudentScoreDetails] = useState([]);
  const [dateTimeBasedUniqueKey, setDateTimeBasedUniqueKey] = useState(
    new Date().getTime().toString()
  );
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  const [getSingleStudentAssignmentsScoreMutation] = useGetSingleStudentAssignmentsScoreMutation();
  const [getTeacherReportOptions] = useGetTeacherReportOptionsMutation();

  useEffect(() => {
    checkContextData();
    getReportOptions();
  }, []);

  const checkContextData = () => {
    if(selectedCourseId == -1)
      navigate(-1);
  };

  const getReportOptions = async () => {
    let response = await getTeacherReportOptions().unwrap();
    setUnansweredAsIncorrect(response.incompleteScoring);
    setRetryScore(response.retryScore);
    setDisplayName(response.displayName);
    setDisplayId(response.displayId);

    await handleReportData(response.retryScore, response.incompleteScoring);
    setIsLoadingReportData(false);
  };

  const handleReportData = async (retryScore, incompleteScoring) => {
    if(selectedCourseId == -1)
      return;

    try{
      const options={
        studentId: selectedStudentId,
        startDate: DateFormatter.toShortDate2(startDate),
        endDate: DateFormatter.toShortDate2(endDate),
        courseId: selectedCourseId,
        isAssessment: isAssessment
      };

      let response = await getSingleStudentAssignmentsScoreMutation(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        setStudentScoreDetails(response.data.studentDetails[0].scoreDetails);
        updateAssignmentDetails(response.data.assignmentDetails, response.data.studentDetails[0].scoreDetails, retryScore, incompleteScoring);
        setReportDate(response.data.reportDate);
      }
    } catch (error) {
      console.log(error);
    }
    setDateTimeBasedUniqueKey(new Date().getTime().toString());
  };

  const updateAssignmentDetails = (assignments, studentScores, retryScore, incompleteScoring) => {
    assignments = assignments ?? assignmentDetails;
    studentScores = studentScores ?? studentScoreDetails;

    let updatedAssignmentDetails = assignments.map(assignment => {
      let scoreDetail = studentScores.find(score => score.assignmentId === assignment.assignmentId);
      let score = ScoreCalculator(retryScore, incompleteScoring ? 1 : 0, scoreDetail.assignmentScoreCount, scoreDetail.correct, scoreDetail.incorrect, scoreDetail.correctOnRetry, scoreDetail.crPoints, scoreDetail.crAnswered, scoreDetail.crNotAnswered, scoreDetail.crMaxPoints, scoreDetail.statusNewCount);

      return {
        ...assignment,
        
        count: scoreDetail.assignmentScoreCount,
        correct: scoreDetail.correct,
        incorrect: incompleteScoring ? scoreDetail.incorrect + scoreDetail.statusNewCount : scoreDetail.incorrect,
        correctOnRetry: scoreDetail.correctOnRetry,
        crAnswered: incompleteScoring ? scoreDetail.crAnswered + scoreDetail.crNotAnswered : scoreDetail.crAnswered,
        score: (score>=0 && score<=100) ? `${score}%` : score
      };
    });
    setAssignmentDetails(updatedAssignmentDetails);
  };

  const exportHandler = () => {
    const timePeriod = `${DateFormatter.toShortDate2(startDate)} to ${DateFormatter.toShortDate2(endDate)}`;
    const reportDetails = {
      Report : `${isAssessment ? 'Assessment' : 'Assignment'} Scores for ${!ferpaEnabled ? 'name hidden' : studentName}`,
      Course : courseName,
      'Time Period': timePeriod,
      'Report Date': DateFormatter.toLongDateTime(reportDate, true)
    };    

    const reportData = [];
    const scoreDescription = [];

    assignmentDetails.forEach((report) => {
      reportData.push({
        'Assignment Name': report.description,
        'Last Assigned': DateFormatter.toShortDate2(report.assignDate),
        Count: report.count,
        Correct: report.correct,
        Incorrect: report.incorrect,
        'Correct On Retry': report.correctOnRetry,
        'CR Answered': report.crAnswered,
        Score: report.score
      });      
    });

    scoreDescription.push({
      'Not Scored': 'NS',
      'Not Started': 'N',
      'In Progress': 'P'
    });    
    
    const newData = {
      ReportOptions : reportDetails,
      ',' : reportData,
      '' : scoreDescription
    };

    const newDataWithReportOptionsFirst = reorderObjectProperties(newData, 'ReportOptions');
    const csvData = convertToCSV(newDataWithReportOptionsFirst);
    downloadCSV(csvData, 'All Assignment Single Student Score' );
    setIsCsvGenerating(false);
  };

  const printHandler = async () => {
    if(isPdfGenerating) return;

    const contentWrapper = document.getElementById('reportWrapper');
    const htmlString = contentWrapper.innerHTML;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const ferpaContainer = tempContainer.querySelector('#ferpaContainer');
    if (ferpaContainer) {
      ferpaContainer.classList.add('d-none');
    }

    const buttonElements = tempContainer.querySelectorAll('button');
    buttonElements.forEach((svgElement) => {
      svgElement.classList.add('d-none');
    });

    const reportOptionBtn = tempContainer.querySelector('#reportOptionsDiv');
    if(reportOptionBtn) {
      reportOptionBtn.classList.add('d-none');
    }

    const reportOptions = tempContainer.querySelector('#reportOptions');
    if(reportOptions) {
      reportOptions.classList.remove('d-none');
    }

    await printPDF('All_Assignment_Single_Student_Score', tempContainer.innerHTML, PageSize.A3);
  };

  const sortButtons = (currentSortId) => {
    return (
      <>
        <button className="assignment-sort-btn sort-btn row p-1 mt-2">
          <FontAwesomeIcon icon={faCaretUp} className={`p-0 ${sortId === currentSortId && isAscending ? 'enabled-caret' : ''}`} />
          <FontAwesomeIcon icon={faCaretDown} className={`p-0 align-caret-down ${sortId === currentSortId && !isAscending ? 'enabled-caret' : ''}`} />
        </button>
      </>
    );
    
  };

  const handleSort = async (currentSortId) => {
    let sortAsc = true;
    if (currentSortId === sortId)
      sortAsc = !isAscending;
    setSortId(currentSortId);

    let sortedAssignments = await ResourceUtils.customSort(assignmentDetails, currentSortId, sortAsc);
    setAssignmentDetails(sortedAssignments);
    setIsAscending(sortAsc);
  };

  const getScoreLabel = (score) => {
    const colorItem = colorCode.find(item => item.label == score);
    return (
      <td key={'score'} className='text-center'>
        {colorItem ?
          <span className='d-flex justify-content-center'>
            <div className='rounded-circle circleBtn' style={{ backgroundColor: colorItem.backgroundColor }}>
              {colorItem.icon}
            </div>
          </span>
          : score}
      </td>
    );
  };

  return (
    <div className="white-card">
      <div id="reportWrapper">
        <div className="d-flex align-items-center justify-content-between pb-4">
          <h2 className="primary-title mb-md-0">{`${isAssessment ? 'Assessment' : 'Assignment'} Scores for ${!ferpaEnabled ? 'name hidden' : studentName}`}</h2>
          <div id="ferpaContainer" className="d-flex justify-content-end align-items-center gap-3">
            <FERPA />
            <button id="back_button" onClick={() => window.history.back()} className="btn btn-width btn-castleWhite">
              Back
            </button>
            <button id="export_button" onClick={async() => {setIsCsvGenerating(true); exportHandler();}} className="btn btn-width btn-castleOrange p-0" disabled={isCsvGenerating || isLoadingReportData}>
              {isCsvGenerating == true ? 'Generating CSV' : 'Export'}
            </button>
            <ProgressButton
              isLoading={isPdfGenerating}
              onClick={printHandler}
              loadingLabel="Generating PDF"
              label="Print to PDF"
              className="btn btn-castleRed p-0"
              disabled={isLoadingReportData}
            />
          </div>
        </div>
        <ReportHeader
          isLoading={isLoadingReportData}
          startDate={startDate}
          endDate={endDate}
          reportDate={reportDate}
          courseName={courseName}
        />
        <div className='align-items-baseline d-flex justify-content-between pt-3'>
          <div id="reportOptionsDiv" className='d-flex fw-medium text-castleOrange cursor-pointer report-button'
            onClick={() => setExpand(!expand)}>
            Report Options
            <div className='align-items-center cursor-pointer d-flex'>
              <FontAwesomeIcon className='fs-7 ms-2' icon={expand ? faMinus : faPlus} />
            </div>
          </div>
          <div id="scoringDescription" className='d-flex pb-4'>
            {colorCode.map((item) => (
              <div key={item.name} className='align-items-center d-flex px-4'>
                <div className='d-flex rounded-circle justify-content-center' style={{ backgroundColor: item.backgroundColor }}>
                  {item.icon}
                </div>
                <span className='px-2'>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
        <ReportOptions
          key={dateTimeBasedUniqueKey}
          expand={expand}
          isSingleStudent={true}
          retryScore={retryScore}
          setRetryScore={setRetryScore}
          unansweredAsIncorrect ={unansweredAsIncorrect }
          setUnansweredAsIncorrect={setUnansweredAsIncorrect}
          displayName={displayName}
          setDisplayName={setDisplayName}
          displayId={displayId}
          setDisplayId={setDisplayId}
          updateAssignmentDetails={updateAssignmentDetails}
        />
        <div className='pt-4'>
          <div className='custom-table'>
            <h2 className='fw-medium pb-3'>Assignments</h2>
            <div className='assignments-scroll-bar max-h-550 custom-table table-responsive p-0'>
              <table className='table reportTable'>
                {
                  assignmentDetails?.length > 0 && !isLoadingReportData ? (
                    <>
                      <thead className='thead-light align-middle'>
                        <tr>
                          <th id='description' scope="col" className='height-unset cursor-pointer text-start' onClick={(e) => handleSort(e.currentTarget.id)}>
                            <label className="fs-14 mb-0">Assignment Name</label>
                            {sortButtons('description')}
                          </th>
                          <th id='assignDate' scope="col" className='fw-medium w-min-max-100 text-wrap' onClick={(e) => handleSort(e.currentTarget.id)}>
                          Last Assigned
                            {sortButtons('assignDate')}
                          </th>
                          <th id='count' scope="col" className='fw-medium text-center'>
                          Count
                          </th>
                          <th id='correct' scope="col" className='fw-medium text-center'>
                          Correct
                          </th>
                          <th id='incorrect' scope="col" className='fw-medium text-center'>
                          Incorrect
                          </th>
                          <th id='correctOnRetry' scope="col" className='fw-medium text-center text-wrap'>
                          Correct On Retry
                          </th>
                          <th id='crAnswered' scope="col" className='fw-medium text-center'>
                          CR Answered
                          </th>
                          <th id='score' scope="col" className='fw-medium text-center w-10' onClick={(e) => handleSort(e.currentTarget.id)}>
                          Score
                            {sortButtons('score')}
                          </th>
                          <th>
                            {'   '}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentDetails?.map((item, index) => (
                          <tr key={item.assignmentId}>
                            <td className='w-min-max-250 text-truncate'>{item.description}</td>
                            <td className='text-center'>{DateFormatter.toShortDate2(item.assignDate)}</td>
                            <td className='text-center'>{item.count}</td>
                            <td className='text-center'>{item.correct}</td>
                            <td className='text-center'>{item.incorrect}</td>
                            <td className='text-center'>{item.correctOnRetry}</td>
                            <td className='text-center'>{item.crAnswered}</td>
                            {getScoreLabel(item.score)}
                            <td className='text-center'>
                              {(item.correct + item.incorrect + item.crAnswered > 0) &&
                              <div className='d-flex justify-content-center align-items-center gap-2'>
                                <OverlayTrigger
                                  key={`viewReportToolTip${item.assignmentId}`}
                                  placement="top"
                                  overlay={<Tooltip className="custom-tooltip">View Report</Tooltip>}>
                                  <button
                                    id={'viewReport' + index}
                                    className='btn grid-icon-btn info-icon-btn'
                                    onClick={() => 
                                      navigate(`${item.assignmentId}/student_session_report?studentId=${selectedStudentId}&incompleteScoring=${false}`)
                                    }>
                                    <FontAwesomeIcon icon={faChartArea} />
                                  </button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  key={`viewResponsesToolTip${item.assignmentId}`}
                                  placement="top"
                                  overlay={<Tooltip className="custom-tooltip">View Responses</Tooltip>}>
                                  <button
                                    id={'viewResponses' + index}
                                    className='btn grid-icon-btn success-icon-btn'
                                    onClick={() => 
                                      navigate(`responses/${item.assignmentId}/${isAssessment ? ActivityTypes.Assessment : ActivityTypes.Assignment}/${selectedStudentId}`)
                                    }>
                                    <FontAwesomeIcon icon={faMessagesQuestion} />
                                  </button>
                                </OverlayTrigger>
                              </div>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : isLoadingReportData ? (
                    <>
                      <TableSkeleton
                        colCount={8}
                        rowCount={6}
                      />
                    </>
                  ) :
                    <p className='text-danger'>There are no results for the selected report options.</p>
                }
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleStudentScoreInfo;
